import React from 'react';
import { connect } from 'react-redux';
import { Route, withRouter, RouteComponentProps } from 'react-router-dom';
import { values, find } from 'lodash';
import { DndProvider } from 'react-dnd';
import MultiBackend from 'react-dnd-multi-backend';
import HTML5toTouch from 'react-dnd-multi-backend/lib/HTML5toTouch';
import { Transition } from 'react-transition-group';

import * as Types from 'types';
import { PlaylistList } from 'playlists/PlaylistList';
import { SelectedPlaylistContainer } from 'playlists/SelectedPlaylistContainer';
import { PlaylistArtworkSelectorContainer } from 'playlists/PlaylistArtworkSelectorContainer';
import { fetchCurrentUserPlaylists } from 'features/playlists/actions';
import {
  filterSavedPlaylists,
  isPlaylistEditable,
  orderPlaylists,
} from 'features/playlists/helpers';
import { PlaylistArtworkDraggablePreview } from 'playlists/PlaylistArtworkDraggablePreview';

type StateProps = {
  playlists: Types.Playlist[];
  defaultPlaylistId?: string;
  currentUserId?: number;
};

type DispatchProps = {
  fetchCurrentUserPlaylists: () => Promise<void>;
};

type HOCProps = RouteComponentProps<{
  id: string;
}>;

type Props = StateProps & DispatchProps & HOCProps;

class Playlists extends React.PureComponent<Props> {
  componentDidMount() {
    this.props.fetchCurrentUserPlaylists().then(() => {
      if (!this.props.match.params.id) {
        this.redirectToDefaultPlaylist();
      }
    });
  }

  componentDidUpdate(prevProps: Props) {
    const { id } = this.props.match.params;
    const idChanged = id !== prevProps.match.params.id;
    if (idChanged && !id) {
      this.redirectToDefaultPlaylist();
    }
  }

  onEditPlaylistClicked(playlistId: number) {
    this.props.history.push(Routes.edit_vault_playlist_path(playlistId));
  }

  onPlaylistTitleClick(playlistId: number) {
    this.props.history.push(Routes.vault_playlist_path(playlistId));
  }

  redirectToDefaultPlaylist() {
    if (this.props.defaultPlaylistId) {
      this.props.history.push(Routes.vault_playlist_path(this.props.defaultPlaylistId));
    }
  }

  isEditMode() {
    return this.props.match.path === Routes.edit_vault_playlist_path(':id');
  }

  isEditable(playlistId: string) {
    const playlist = find(this.props.playlists, { id: playlistId });
    return isPlaylistEditable(playlist, this.props.currentUserId || 0);
  }

  render() {
    const { playlists } = this.props;
    if (playlists.length === 0) {
      return null;
    }
    const playlistId = `${this.props.match.params.id}`;
    const showArtworkSelector = this.isEditMode() && this.isEditable(playlistId);

    return (
      <DndProvider backend={MultiBackend} options={HTML5toTouch}>
        <div className='row mb-lg-6'>
          <div className='col-auto playlists-container__playlists'>
            <PlaylistList playlists={this.props.playlists} playlistId={playlistId} />
          </div>
          <Route
            path={`${Routes.vault_playlist_path(':id')}/(edit)?`}
            render={() => (
              <div className='col'>
                <div className='row'>
                  <div className='col col-lg-7'>
                    <SelectedPlaylistContainer
                      editMode={this.isEditMode()}
                      playlistId={playlistId}
                    />
                  </div>
                  {showArtworkSelector && (
                    <Transition timeout={1} appear in>
                      {(state) => (
                        <PlaylistArtworkSelectorContainer
                          className={`col col-lg-5 css-transition-slide css-transition--${state}`}
                        />
                      )}
                    </Transition>
                  )}
                </div>
              </div>
            )}
          />
          <PlaylistArtworkDraggablePreview />
        </div>
      </DndProvider>
    );
  }
}

const mapStateToProps = ({ playlists, currentUser }: Types.RootState): StateProps => {
  const playlistsArray = values(playlists.byId);
  let defaultPlaylist = find(playlistsArray, (playlist) => playlist.type === 'Master');
  if (!defaultPlaylist && playlistsArray.length > 0) {
    defaultPlaylist = playlistsArray[0];
  }

  return {
    defaultPlaylistId: defaultPlaylist && defaultPlaylist.id,
    playlists: orderPlaylists(filterSavedPlaylists(playlistsArray), currentUser.user?.id),
    currentUserId: currentUser.user?.id,
  };
};

const mapDispatchToProps = {
  fetchCurrentUserPlaylists,
};

export const PlaylistsContainer = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(Playlists),
);
